import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AmountInput from "@/components/AmountInput";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Wallet, Pencil, Info, Send, Eraser } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

/**
 * Cash flow summary card displayed above the cash sheet.
 * Shows: saldo iniziale → + incassi liquidi → − abbuoni → − versamenti (uscita)
 *        [+ versamenti in entrata (only for agency sheet)] = saldo finale.
 *
 * Lets the owner adjust the starting balance manually (audit-logged).
 */
export default function CashFlowCard({ flow, sheetId, onUpdated, canEdit = true, isAgency = false }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const isLibero = (user?.versamento_mode || "polizze") === "importo_libero";
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [azzeraOpen, setAzzeraOpen] = useState(false);
  const [azzerando, setAzzerando] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open || !isAgency || !sheetId) return;
    api.get(`/cash-flow/sheets/${sheetId}/adjust-initial/preview`)
      .then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [open, isAgency, sheetId]);

  const openVersamentiDetail = async () => {
    setDetailOpen(true);
    if (!detail) {
      setLoadingDetail(true);
      try {
        const r = await api.get(`/cash-flow/sheets/${sheetId}/versamenti-entrata`);
        setDetail(r.data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  if (!flow) return null;

  const submitAdjust = async () => {
    const num = Number(val);
    if (Number.isNaN(num)) {
      toast.error("Inserisci un valore numerico");
      return;
    }
    const residuo = preview?.applies ? Number(preview.residuo || 0) : 0;
    if (residuo > 0 && !window.confirm(
      `Verranno azzerati ${formatEUR(residuo)} da versare dei giorni precedenti (liquidità di agenzia non ancora versata). Continuare?`,
    )) return;
    setSaving(true);
    try {
      const r = await api.put(`/cash-flow/sheets/${sheetId}/adjust-initial`, {
        saldo_iniziale: num,
        note,
      });
      const az = r.data?.riallineamento?.azzerato || 0;
      toast.success(az > 0 ? `Saldo iniziale aggiornato · azzerati ${formatEUR(az)} dei giorni precedenti` : "Saldo iniziale aggiornato");
      setPreview(null);
      setOpen(false);
      setVal("");
      setNote("");
      if (onUpdated) await onUpdated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const submitAzzera = async () => {
    setAzzerando(true);
    try {
      const r = await api.post("/versamenti/azzera-cassa", { note: "Azzeramento cassa dal saldo iniziale" });
      const az = r.data?.azzerato || 0;
      toast.success(`Cassa azzerata (${az.toLocaleString("it-IT", { style: "currency", currency: "EUR" })} registrati come versati)`);
      setAzzeraOpen(false);
      setOpen(false);
      if (onUpdated) await onUpdated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAzzerando(false);
    }
  };

  const auditCount = Array.isArray(flow.audit) ? flow.audit.length : (flow.audit ? 1 : 0);

  return (
    <Card className="border-emerald-200 bg-emerald-50/40 p-3" data-testid="cash-flow-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-emerald-600 text-white flex items-center justify-center">
            <Wallet className="w-3.5 h-3.5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold leading-tight">Flusso di cassa</div>
            <div className="font-heading text-sm font-semibold text-slate-900 leading-tight">
              {isAgency ? "Cassa di agenzia" : "Liquidità giornaliera"}
            </div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => nav(isAgency ? "/admin/versamento" : "/versamento")}
              data-testid="cash-flow-create-versamento"
            >
              <Send className="w-3 h-3 mr-1" /> Crea versamento
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" data-testid="cash-flow-edit-initial">
                  <Pencil className="w-3 h-3 mr-1" /> Modifica saldo iniziale
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Modifica saldo iniziale</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Saldo iniziale attuale: <strong>{formatEUR(flow.saldo_iniziale)}</strong>
                  {flow.overridden && <span className="ml-2 text-xs text-amber-700">(modificato manualmente)</span>}
                </p>
                <div>
                  <label className="text-sm font-medium text-slate-700">Nuovo saldo iniziale (€)</label>
                  <AmountInput
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    placeholder="0,00"
                    data-testid="cash-flow-input-initial"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Motivazione (opzionale)</label>
                  <Input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Es. conta cassa apertura agenzia"
                    data-testid="cash-flow-input-note"
                  />
                </div>
                <p className="text-xs text-slate-500 inline-flex items-start gap-1">
                  <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  La modifica verrà registrata in audit: si vedrà chi l&apos;ha effettuata, quando e l&apos;importo precedente.
                </p>
                {preview?.applies && preview.residuo > 0 && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="cash-flow-realign-warning">
                    Salvando il nuovo saldo verranno azzerati <strong data-testid="cash-flow-realign-amount">{formatEUR(preview.residuo)}</strong> da
                    versare dei giorni precedenti: risulteranno riallineati e non si sommeranno più a &quot;Da incassare&quot;.
                  </div>
                )}
                {isLibero && (
                  <div className="mt-4 pt-3 border-t border-slate-200">
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
                      <div className="text-xs font-semibold text-amber-900 flex items-center gap-1.5 mb-1">
                        <Eraser className="w-3.5 h-3.5" /> Modalità versamento libero
                      </div>
                      <p className="text-xs text-amber-800 leading-relaxed">
                        Se hai già versato/depositato la cassa storica al di fuori del sistema, puoi
                        registrare tutto il residuo come versato con un solo click. Il calcolo
                        &quot;Da versare&quot; verrà riallineato a zero.
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-2 border-amber-400 text-amber-900 hover:bg-amber-100 hover:text-amber-900"
                        onClick={() => setAzzeraOpen(true)}
                        data-testid="cash-flow-azzera-cassa"
                      >
                        <Eraser className="w-3.5 h-3.5 mr-1.5" /> Azzera cassa
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                <Button onClick={submitAdjust} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="cash-flow-save-initial">
                  {saving ? "Salvataggio…" : "Salva"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Row label="Saldo iniziale" value={flow.saldo_iniziale} testId="flow-iniziale" muted />
        <Row label="+ Incassi liquidi" value={flow.incassi_liquidi} testId="flow-incassi" positive />
        <Row label="− Abbuoni" value={flow.abbuoni_liquidi} testId="flow-abbuoni" negative />
        <Row label="− Versamenti usciti" value={flow.versamenti_uscita} testId="flow-versamenti-uscita" negative />
        {isAgency && (
          <button
            type="button"
            onClick={openVersamentiDetail}
            className="rounded-md bg-white border border-slate-100 px-2 py-1.5 text-left hover:border-emerald-400 hover:bg-emerald-50 transition-colors"
            data-testid="flow-versamenti-entrata"
            title="Clicca per vedere il dettaglio dei versamenti"
          >
            <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold leading-tight flex items-center gap-1">
              + Versamenti in entrata <Info className="w-3 h-3" />
            </div>
            <div className="font-heading font-bold text-sm mt-0.5 font-mono-num leading-tight text-emerald-700">
              {formatEUR(flow.versamenti_entrata)}
            </div>
          </button>
        )}
      </div>

      {/* Dettaglio versamenti in entrata */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="versamenti-detail-dialog">
          <DialogHeader>
            <DialogTitle>Dettaglio versamenti in entrata</DialogTitle>
            <div className="text-xs text-slate-500">
              Elenco dei versamenti dei collaboratori che hanno destinazione &quot;in agenzia&quot; e quindi sommano al saldo di cassa. I versamenti su C/C agenzia sono esclusi.
            </div>
          </DialogHeader>
          {loadingDetail && <div className="text-sm text-slate-500 py-6 text-center">Caricamento…</div>}
          {!loadingDetail && detail && (
            <div className="max-h-96 overflow-y-auto">
              {detail.dettaglio.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Nessun versamento in entrata per questo giorno.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2">Collaboratore</th>
                      <th className="text-center px-3 py-2">N. Polizze</th>
                      <th className="text-right px-3 py-2">Liquido sommato</th>
                      <th className="text-right px-3 py-2">Totale versato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.dettaglio.map((v) => (
                      <tr key={v.versamento_id} className="border-t border-slate-100" data-testid={`vd-row-${v.versamento_id}`}>
                        <td className="px-3 py-2 font-medium text-slate-900">{v.user_name}</td>
                        <td className="px-3 py-2 text-center text-slate-600">{v.n_polizze}</td>
                        <td className="px-3 py-2 text-right font-mono-num text-emerald-700 font-semibold">{formatEUR(v.importo_liquido)}</td>
                        <td className="px-3 py-2 text-right font-mono-num text-slate-600">{formatEUR(v.totale_versamento)}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                      <td className="px-3 py-2 text-slate-900" colSpan={2}>TOTALE</td>
                      <td className="px-3 py-2 text-right font-mono-num text-emerald-700">{formatEUR(detail.totale_liquido_entrata)}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDetailOpen(false)} data-testid="vd-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma Azzera cassa (solo modalità libero) */}
      <Dialog open={azzeraOpen} onOpenChange={setAzzeraOpen}>
        <DialogContent data-testid="azzera-cassa-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eraser className="w-5 h-5 text-amber-700" /> Azzera cassa
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              Questa azione registra come <strong>già versato</strong> tutto il residuo storico
              in tua cassa (in modalità versamento libero). L&apos;importo &quot;Da versare&quot;
              mostrato nella dashboard verrà riallineato a zero.
            </p>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 leading-relaxed">
              <strong>Cosa succede:</strong>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>Verrà creato un versamento libero interno con la cifra pari al residuo attuale</li>
                <li>Le entries storiche non versate verranno segnate come versate</li>
                <li>L&apos;operazione è tracciata (audit)</li>
              </ul>
            </div>
            <p className="text-xs text-red-700">
              <strong>Attenzione</strong>: non ricalcola gli incassi esistenti. Serve solo per
              riallineare il sistema alla cassa fisica reale.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAzzeraOpen(false)} data-testid="azzera-cassa-cancel">
              Annulla
            </Button>
            <Button
              onClick={submitAzzera}
              disabled={azzerando}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="azzera-cassa-confirm"
            >
              {azzerando ? "Azzeramento…" : "Conferma azzeramento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-3 pt-3 border-t border-emerald-200 flex items-center justify-between">
        <div className="text-xs text-slate-700">
          {flow.overridden ? "Saldo iniziale modificato manualmente" : "Saldo iniziale calcolato dal giorno precedente"}
          {auditCount > 0 && <span className="ml-2 text-[10px] text-slate-500">({auditCount} rettifiche)</span>}
        </div>
        <div className="text-right">
          <div className="text-[10px] text-emerald-700 uppercase font-semibold tracking-wider leading-tight">Saldo finale</div>
          <div className="font-heading text-lg font-bold text-emerald-700 font-mono-num leading-tight" data-testid="flow-saldo-finale">
            {formatEUR(flow.saldo_finale)}
          </div>
        </div>
      </div>
    </Card>
  );
}

function Row({ label, value, testId, positive, negative, muted }) {
  const cls =
    positive ? "text-emerald-700" :
    negative ? "text-red-600" :
    muted ? "text-slate-600" : "text-slate-900";
  return (
    <div className="rounded-md bg-white border border-slate-100 px-2 py-1.5" data-testid={testId}>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold leading-tight">{label}</div>
      <div className={`font-heading font-bold text-sm mt-0.5 font-mono-num leading-tight ${cls}`}>
        {formatEUR(value)}
      </div>
    </div>
  );
}
